import {
  DSAttendanceMonitoringType,
  MONITORING_TYPE_LABELS
} from '@/constants/ds-attendance.constants'

/**
 * Regras de colunas do modelo de folha (#3052). O modelo define só colunas (RN-09 do PRD 32);
 * as fixas dependem da modalidade e as adicionais vêm de uma lista fechada (Q2 do ledger
 * `docs/discovery/sheet-template-modal-v2`).
 */
export const ADDITIONAL_COLUMN_OPTIONS = ['Setor', 'Especialidade', 'Valor do plantão'] as const

const SIGNATURE_IN = 'Ass. e Carimbo Entrada'
const SIGNATURE_OUT = 'Ass. e Carimbo Saída'

export function fixedColumnsFor(monitoringType: DSAttendanceMonitoringType): string[] {
  const withSignatures = monitoringType !== DSAttendanceMonitoringType.EXTERNAL_APP
  return [
    'Nome do médico',
    'CRM',
    'Data',
    'Jornada início',
    'Jornada Fim',
    'Entrada',
    ...(withSignatures ? [SIGNATURE_IN] : []),
    'Saída',
    ...(withSignatures ? [SIGNATURE_OUT] : [])
  ]
}

const ALL_FIXED_COLUMNS = new Set(fixedColumnsFor(DSAttendanceMonitoringType.FOLHA))

export const isFixedColumnName = (name: string): boolean => ALL_FIXED_COLUMNS.has(name)

export type ColumnTypeLabel = 'Data' | 'Hora' | 'Checkbox' | 'Número' | 'Texto'

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()

/** Selo somente leitura do modal — ajuda de leitura, não entra no payload (Q9). */
export function columnTypeLabel(name: string): ColumnTypeLabel {
  const label = normalize(name)
  if (label === 'data') return 'Data'
  if (label.includes('ass.') || label.includes('carimbo')) return 'Checkbox'
  if (['jornada', 'entrada', 'saida'].some((part) => label.includes(part))) return 'Hora'
  if (label === 'valor do plantao') return 'Número'
  return 'Texto'
}

export interface LoadedTemplateColumns {
  /** Colunas não fixas, na ordem salva (inclui nomes livres e vazios de modelos antigos). */
  additional: string[]
  /** Fixas de outra modalidade presentes no modelo salvo — saem ao salvar. */
  removedFixed: string[]
  /** A ordem salva difere de "fixas da modalidade + adicionais" (desconsiderando removidas e faltantes). */
  orderChanged: boolean
}

export function loadTemplateColumns(
  saved: string[],
  monitoringType: DSAttendanceMonitoringType
): LoadedTemplateColumns {
  const fixed = fixedColumnsFor(monitoringType)
  const removedFixed = saved.filter((name) => isFixedColumnName(name) && !fixed.includes(name))
  const additional = saved.filter((name) => !isFixedColumnName(name))
  const kept = saved.filter((name) => !removedFixed.includes(name))
  const expected = [...fixed.filter((name) => saved.includes(name)), ...additional]
  const orderChanged =
    kept.length !== expected.length || kept.some((name, index) => name !== expected[index])
  return { additional, removedFixed, orderChanged }
}

export function buildTemplateColumns(
  monitoringType: DSAttendanceMonitoringType,
  additional: string[]
): string[] {
  // Repetidas saem mantendo a primeira ocorrência (defesa contra o type-ahead do VSelect, Q6).
  const unique = [...new Set(additional.filter((name) => name.trim() !== ''))]
  return [...fixedColumnsFor(monitoringType), ...unique]
}

const quoteList = (names: string[]) => {
  const quoted = names.map((name) => `"${name}"`)
  return quoted.length > 1
    ? `${quoted.slice(0, -1).join(', ')} e ${quoted[quoted.length - 1]}`
    : quoted[0]
}

/** Aviso do modal para modelo salvo fora do formato (Q5/Q10). Fixas faltantes não geram frase. */
export function formatNotice(
  loaded: LoadedTemplateColumns,
  monitoringType: DSAttendanceMonitoringType
): string[] {
  const lines: string[] = []
  if (loaded.orderChanged) {
    lines.push(
      'A ordem das colunas deste modelo foi ajustada: as colunas fixas ficam sempre primeiro. Ao salvar, a folha passará a usar esta ordem.'
    )
  }
  if (loaded.removedFixed.length) {
    const modality = MONITORING_TYPE_LABELS[monitoringType]
    const names = quoteList(loaded.removedFixed)
    lines.push(
      loaded.removedFixed.length === 1
        ? `A coluna ${names} não faz parte da modalidade ${modality} e será removida ao salvar.`
        : `As colunas ${names} não fazem parte da modalidade ${modality} e serão removidas ao salvar.`
    )
  }
  return lines
}
